<template>
  <nav>
    <AlertMessages
      v-if="alertValue"
      :alert-type="alertType" 
      :alert-msg="alertMsg"
      @reset="alertValue = false"
    />
    <v-row>
      <v-col
        cols="11"
        class="mx-auto"
      >
        <p 
          :class="mobile() ? 'text-h5 font-weight-bold ml-16 mr-16' : 'text-h5 font-weight-bold'"
          :align="mobile() ? 'center' : 'left'"
        >
          {{ $t('spaceSAMLTitle') }}
        </p>
        <p class="text-body-1 greytext--text d-none d-sm-block">
          {{ $t('spaceSAMLText') }}
        </p>
      </v-col>
    </v-row>
    <v-row
      v-if="!loadingData && idps.length > 0"
      class="saml"
    > 
      <v-col
        cols="12"
        sm="8"
        md="6"
        offset-sm="2"
        offset-md="3"
      >
        <p class="text-h5 text-center font-weight-bold">
          {{ name }}
        </p>
        <v-card
          elevation="2"
          rounded="xxl"
          class="mb-5 pa-4"
        >
          <v-btn
            data-testid="edit-options-button"
            icon
            absolute
            right
            top
            class="mt-n2 mr-n2"
            @click="toggleOptionsEditMode"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-card-title class="text-h5 font-weight-bold mb-4 no-break">
            {{ $t('options') }}
          </v-card-title>
          <v-card-text class="px-15 checkbox-align-start">
            <v-checkbox
              v-model="regularLoginDisabled"
              :label="$t('samlRegularLoginDisabled')"
              class="ma-0 px-2"
              data-testid="option1-checkbox"
              :disabled="readonlyOptions"
            />
            <v-checkbox
              v-model="enableLoginButton"
              :label="$t('samlLoginButton')"
              class="ma-0 px-2"
              data-testid="option2-checkbox"
              disabled
            />
            <v-text-field
              v-model="buttonText"
              data-testid="button-text-input"
              solo
              hide-details="auto"
              elevation="6"
              class="rounded-xxl text-body-2 mb-5"
              :placeholder="$t('samlLoginButtonPlaceholder')"
              :disabled="readonlyOptions"
            />
          </v-card-text>
          <v-card-actions
            v-if="!readonlyOptions"
            class="flex-column  flex-sm-column flex-md-row align-end"
          >
            <v-spacer />
            <v-btn
              ref="cancel"
              data-testid="cancel-button"
              min-width="100"
              dark
              elevation="0"
              class="rounded-xxl text-none px-4 mb-2 mb-md-0"
              @click="resetOptions"
            >
              {{ $t('cancel') }}
            </v-btn>
            <v-btn
              ref="submit"
              data-testid="submit-button"
              color="accent"
              min-width="100"
              dark
              elevation="0"
              class="rounded-xxl text-none px-4"
              @click="editOptions"
            >
              {{ $t('save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
        <v-card
          elevation="2"
          rounded="xxl"
          class="mb-5 pa-4"
        >
          <v-btn
            data-testid="edit-idp-settings-button"
            icon
            absolute
            right
            top
            class="mt-n2 mr-n2"
            @click="toggleIdpSettingsEditMode"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-card-title class="text-h5 font-weight-bold mb-4 no-break">
            {{ $t('idpSettings') }}
          </v-card-title>
          <v-card-text class="px-15">
            <v-form
              ref="form"
              v-model="valid"
              @submit.prevent
            >
              <p class="font-weight-bold text-h6 darktext--text">
                {{ $t('labelEntityId') }}
              </p>
              <v-text-field
                v-model="idpEntityId"
                data-testid="entity-id-input"
                :disabled="readonlyIdp"
                solo
                hide-details="auto"
                elevation="6"
                class="rounded-xxl text-body-2 mb-5"
                :rules="entityRules"
                :error-messages="errorsEntityId"
              />
              <p class="font-weight-bold text-h6 darktext--text">
                {{ $t('labelSSOURL') }}
              </p>
              <v-text-field
                v-model="idpSSOURL"
                data-testid="sso-url-input"
                :disabled="readonlyIdp"
                solo
                hide-details="auto"
                elevation="6"
                class="rounded-xxl text-body-2 mb-5"
                :rules="ssoRules"
                :error-messages="errorsSSO"
              />
              <p class="font-weight-bold text-h6 darktext--text">
                {{ $t('labelCertificate') }}
              </p>
              <v-textarea
                v-model="certificate"
                data-testid="certificate-input"
                :disabled="readonlyIdp"
                rounded
                auto-grow
                solo
                name="textarea"
                :rules="certificateRules"
              />
            </v-form>
          </v-card-text>
          <v-card-actions
            v-if="!readonlyIdp"
            class="flex-column  flex-sm-column flex-md-row align-end"
          >
            <v-spacer />
            <v-btn
              ref="cancel"
              data-testid="cancel-button"
              min-width="100"
              dark
              elevation="0"
              class="rounded-xxl text-none px-4 mb-2 mb-md-0"
              @click="resetIdpSettings"
            >
              {{ $t('cancel') }}
            </v-btn>
            <v-btn
              ref="submit"
              data-testid="submit-button"
              color="accent"
              min-width="100"
              dark
              elevation="0"
              class="rounded-xxl text-none px-4"
              @click="editIdpSettings"
            >
              {{ $t('save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
        <v-card
          elevation="2"
          rounded="xxl"
          class="mb-5 pa-4"
          data-testid="service-provider-card"
        >
          <v-card-title class="text-h5 font-weight-bold mb-4 no-break">
            {{ $t('serviceProviderDetails') }}
          </v-card-title>
          <v-card-text class="px-15 mb-5">
            <div class="mb-4">
              <p class="text-h6 darktext--text font-weight-bold mb-0">
                {{ $t('labelEntityId') }}
              </p>
              <span>{{ spEntityId }}</span>
              <v-icon
                class="ml-2"
                data-testid="copy-icon"
                dense
                color="darktext"
                @click="copy(spEntityId)"
              >
                mdi-content-copy
              </v-icon>
            </div>
            <div class="mb-4">
              <p class="text-h6 darktext--text font-weight-bold mb-0">
                {{ $t('labelACSURL') }}
              </p>
              <span>{{ spACSURL }}</span>
              <v-icon
                class="ml-2"
                data-testid="copy-icon"
                dense
                color="darktext"
                @click="copy(spACSURL)"
              >
                mdi-content-copy
              </v-icon>
            </div>
            <div class="mb-4">
              <p class="text-h6 darktext--text font-weight-bold mb-0">
                {{ $t('labelSingleLogoutService') }}
              </p>
              <span>{{ spSingleLogoutService }}</span>
              <v-icon
                class="ml-2"
                data-testid="copy-icon"
                dense
                color="darktext"
                @click="copy(spSingleLogoutService)"
              >
                mdi-content-copy
              </v-icon>
            </div>
            <div class="mb-4">
              <p class="text-h6 darktext--text font-weight-bold mb-0">
                {{ $t('labelNameIdFormat') }}
              </p>
              <span>{{ spNameIdFormat }}</span>
              <v-icon
                class="ml-2"
                data-testid="copy-icon"
                dense
                color="darktext"
                @click="copy(sspNameIdFormat)"
              >
                mdi-content-copy
              </v-icon>
            </div>
            <v-btn
              router
              data-testid="download-button"
              color="accent"
              dark
              elevation="0"
              class="rounded-xxl text-none px-4 mt-5"
              :href="spEntityId" 
              target="_blank"
              download
            >
              {{ $t('downloadMetadata') }}
            </v-btn>
          </v-card-text>
          <v-card-title class="text-h6 font-weight-bold mb-4 no-break">
            {{ $t('attributes') }}
          </v-card-title>
          <v-card-text class="px-15">
            <div>
              <p class="text-h6 darktext--text font-weight-bold mb-0">
                {{ $t('labelMappedAttributes') }}
              </p>
              <div
                v-for="(attribute, index) in mappedAttributes"
                :key="index"
              >
                <span> {{ attribute }} </span>
                <v-icon
                  class="ml-2"
                  data-testid="copy-icon"
                  dense
                  color="darktext"
                  @click="copy(attribute)"
                >
                  mdi-content-copy
                </v-icon>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <PlusButtonWithLink
      v-if="!loadingData && idps.length == 0"
      data-testid="configure-new-saml-button"
      rounded="xxl"
      :link="link"
    />
  </nav>
</template>
<script>
import i18n from '../../plugins/i18n'
import PlusButtonWithLink from '../../components/PlusButtonWithLink'
import AlertMessages  from '../../components/AlertMessages'
import { repeatedMethodsMixin } from '../../mixins/repeatedMethodsMixin'
import { getSpaceByURL, getSAMLConfiguration } from '../../api/spaceApi'
import { editSAMLConfiguration } from '../../api/spaceApi'
import { ruleRequired } from '../../rules/formRules.js';

export default {
    components: {
       PlusButtonWithLink,
       AlertMessages
    },
        mixins: [repeatedMethodsMixin],

    data(){
        return{
            valid: true,
            alertType: '',
            alertMsg: '',
            alertValue:false,
            spaceId: window.$cookies.get('space_id'),
            loadingData: false,
            link: {title: i18n.t('addSAMLConfugurationTitle'), route: '/space/saml/add-configuration'},
            space: window.location.host.split('.')[0],
            idps:[],
            samlSettings: '',
            readonlyIdp: true,
            readonlyOptions: true,
            enableLoginButton: true,
            idpSSOURL: '',
            idpEntityId: '',
            certificate: '',
            errorsEntityId: [],
            errorsSSO: [],
            ssoRules: ruleRequired('labelSSOURL'),
            entityRules: ruleRequired('labelEntityId'),
            certificateRules: ruleRequired('labelCertificate'),
            name: '',
            regularLoginDisabled: false,
            buttonText: '',
            spEntityId: '',
            spACSURL: '',
            spSingleLogoutService: '',
            spNameIdFormat: '',
            mappedAttributes: [],
            id: null
        }
    },
    watch: {
        idpEntityId(){
            this.errorsEntityId = []
        },
        idpSSOURL(){
            this.errorsSSO = []
        },
    },
    async created(){

      if(this.$route.params.successfullyAddedSAMLConfiguration){
        this.alertType = 'success'     
        this.alertMsg = i18n.t('samlConfigurationAddedSuccess')
        this.alertValue = true
      }

      this.loadingData = true
      const responseSpace = await getSpaceByURL(this.space)
      if(responseSpace){
        this.idps = responseSpace.data.data.attributes.idps
        if(this.idps.length > 0){
            try {
                const response = await getSAMLConfiguration(this.idps[0].id)
                if(response.status == 200) {
                  this.samlSettings = response.data.data
                  this.name = this.samlSettings.attributes.display_name,
                  this.id = this.samlSettings.id,
                  this.idpSSOURL = this.samlSettings.attributes.idp_sso_url,
                  this.idpEntityId= this.samlSettings.attributes.idp_entityid,
                  this.certificate= this.samlSettings.attributes.idp_x509,
                  this.regularLoginDisabled = this.samlSettings.attributes.regular_login_disabled == 0 ? false : true,
                  this.buttonText = this.samlSettings.attributes.button_text,
                  this.spEntityId = this.samlSettings.meta.entity_id
                  this.spACSURL = this.samlSettings.meta.assertion_consumer_service
                  this.spSingleLogoutService = this.samlSettings.meta.single_logout_service
                  this.spNameIdFormat = this.samlSettings.meta.name_id_format
                  this.mappedAttributes = this.samlSettings.meta.mapped_attributes
                }
            } catch (e) {
                if(e.response){
                    if(e.response.status == 404) {
                        this.$router.push({name: 'PageNotFound'})
                    }
                    else if(e.response.status == 403){
                        this.$router.push({name: 'PageAccessDenied'})
                    } else {
                        this.alertValue = true
                        this.alertType = 'error'
                        this.alertMsg =  i18n.t('serverError')
                    }
                }            
            }
        }
      }     
      this.loadingData = false
    },
    methods:{
        copy(text){
            const el = document.createElement('textarea');  
            el.value = text
            document.body.appendChild(el);                          
            el.select();                                    
            document.execCommand('copy');                   
            document.body.removeChild(el);
        },
        toggleOptionsEditMode(){
            this.readonlyOptions = !this.readonlyOptions
            this.buttonText = this.samlSettings.attributes.button_text,
            this.regularLoginDisabled = this.samlSettings.attributes.regular_login_disabled == 0 ? false : true
        },
        resetOptions(){
            this.readonlyOptions = true
            this.buttonText = this.samlSettings.attributes.button_text,
            this.regularLoginDisabled = this.samlSettings.attributes.regular_login_disabled == 0 ? false : true
        },
        async editOptions(){
            try {
                let data = {
                    data: {
                        type: "idps",
                        id: this.id, 
                        attributes: {
                            enabled: true,
                            regular_login_disabled: this.regularLoginDisabled,
                            direct_redirect: false,
                            button_text: this.buttonText,
                            idp_entityid: this.samlSettings.attributes.idp_entityid,
                            idp_sso_url: this.samlSettings.attributes.idp_sso_url,
                            idp_x509: this.samlSettings.attributes.idp_x509,
                            space_ids: [this.spaceId]
                        }
                    }
                };
                const response = await editSAMLConfiguration(this.id, data)
                if(response.status == 200) {
                  this.samlSettings = response.data.data
                  this.buttonText = this.samlSettings.attributes.button_text,
                  this.regularLoginDisabled = this.samlSettings.attributes.regular_login_disabled == 0 ? false : true
                  this.readonlyOptions = true
                  this.alertValue = true
                  this.alertType = 'success'
                  this.alertMsg =  i18n.t('samlConfigurationUpdatedSuccess')
                }
            } catch (e) {
                if(e.response){
                    if(e.response.status == 404) {
                        this.$router.push({name: 'PageNotFound'})
                    }
                    else if(e.response.status == 403){
                        this.$router.push({name: 'PageAccessDenied'})
                    } else {
                        this.alertValue = true
                        this.alertType = 'error'
                        this.alertMsg =  i18n.t('serverError')
                    }
                }            
            }
        },
        toggleIdpSettingsEditMode(){
            this.readonlyIdp = !this.readonlyIdp
            this.idpSSOURL = this.samlSettings.attributes.idp_sso_url,
            this.idpEntityId= this.samlSettings.attributes.idp_entityid,
            this.certificate= this.samlSettings.attributes.idp_x509
        },
        resetIdpSettings(){
            this.readonlyIdp = true
            this.idpSSOURL = this.samlSettings.attributes.idp_sso_url,
            this.idpEntityId= this.samlSettings.attributes.idp_entityid,
            this.certificate= this.samlSettings.attributes.idp_x509
        },
        async editIdpSettings(){
            this.$refs.form.validate()
            if(this.valid){
                if(!this.isValidHttpUrl(this.idpSSOURL) || !this.isValidHttpUrl(this.idpEntityId)){
                  if(!this.isValidHttpUrl(this.idpSSOURL)) this.errorsSSO = [i18n.t('SSOURLErrorValid')] 
                  if(!this.isValidHttpUrl(this.idpEntityId)) this.errorsEntityId = [i18n.t('EntityIdURLErrorValid')] 
                }else {
                    try {
                        let data = {
                            data: {
                                type: "idps",
                                id: this.id, 
                                attributes: {
                                    enabled: true,
                                    regular_login_disabled: this.samlSettings.attributes.regular_login_disabled == 0 ? false : true,
                                    direct_redirect: false,
                                    button_text: this.samlSettings.attributes.button_text,
                                    idp_entityid: this.idpEntityId,
                                    idp_sso_url: this.idpSSOURL,
                                    idp_x509: this.certificate,
                                    space_ids: [this.spaceId]
                                }
                            }
                        };
                        const response = await editSAMLConfiguration(this.id, data)
                        if(response.status == 200) {
                          this.samlSettings = response.data.data
                          this.idpSSOURL = this.samlSettings.attributes.idp_sso_url,
                          this.idpEntityId= this.samlSettings.attributes.idp_entityid,
                          this.certificate= this.samlSettings.attributes.idp_x509,
                          this.readonlyIdp = true
                          this.alertValue = true
                          this.alertType = 'success'
                          this.alertMsg =  i18n.t('samlConfigurationUpdatedSuccess')
                        }
                    } catch (e) {
                        if(e.response){
                            if(e.response.status == 404) {
                                this.$router.push({name: 'PageNotFound'})
                            }
                            else if(e.response.status == 403){
                                this.$router.push({name: 'PageAccessDenied'})
                            } else {
                                this.alertValue = true
                                this.alertType = 'error'
                                this.alertMsg =  i18n.t('serverError')
                            }
                        }            
                    }
                }
            }
        },
        isValidHttpUrl(url) {
            let urlCheck;
            try {
                urlCheck = new URL(url);
            } catch (_) {
                return false;  
            }
            return urlCheck.protocol === "http:" || urlCheck.protocol === "https:";
        }
    },
}
</script>